import { borderRadius, palette, spaces } from '@kimuno/theme';
import {
	Autocomplete,
	Box,
	CircularProgress,
	FormLabel,
	IconButton,
	Paper,
	SvgIcon,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React from 'react';
import type { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import getMuiIcon from '../../../../utils/getMuiIcon';
import { type GeneratorConfig, useAISuggestions } from '../../../../utils/useAISuggestions';
import { useId } from '../../../../utils/useId';

type OptionWithConditionalTooltipProps = {
	option: string;
	props: React.HTMLAttributes<HTMLLIElement>;
};

const OptionWithConditionalTooltip: React.FC<OptionWithConditionalTooltipProps> = ({
	option,
	props,
}) => {
	const [showTooltip, setShowTooltip] = React.useState(false);
	const textRef = React.useRef<HTMLLIElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	React.useEffect(() => {
		if (textRef.current) {
			const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
			setShowTooltip(isOverflowing);
		}
	}, [option]);

	return (
		<Tooltip title={option} placement="bottom" arrow disableHoverListener={!showTooltip}>
			<Box
				data-testid="ai-suggestions-option-item"
				component="li"
				{...props}
				ref={textRef}
				role="option"
				sx={{
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				}}
			>
				{option}
			</Box>
		</Tooltip>
	);
};

type AISuggestionsFieldProps = {
	label: string;
	value?: string;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>, reason?: string) => void;
	placeholder?: string;
	dataTestId?: string;
	name?: string;
	registration?: UseFormRegisterReturn;
	error?: FieldError;
	multiline?: boolean;
	maxLength?: number;
	minRows?: number;
	generatorConfig: GeneratorConfig;
	aiParams: Record<string, any>;
	pageSource: string;
};

const AISuggestionsField: React.FC<AISuggestionsFieldProps> = (props) => {
	const { t } = useTranslation();
	const {
		label,
		value,
		onChange,
		placeholder,
		dataTestId,
		name,
		registration,
		error,
		multiline,
		maxLength,
		minRows = 2,
		generatorConfig,
		aiParams,
		pageSource,
	} = props;

	// The Autocomplete owns the input, so react-hook-form is fed a synthetic change
	// event carrying the field name instead of the DOM event.
	const emitChange = (newValue: string, ...reason: [] | [string]) => {
		const syntheticEvent = {
			target: { value: newValue, name },
			currentTarget: { value: newValue, name },
		} as React.ChangeEvent<HTMLInputElement>;
		registration?.onChange(syntheticEvent);
		onChange?.(syntheticEvent, ...reason);
	};

	const aiSuggestions = useAISuggestions({
		generatorConfig,
		params: aiParams,
		initialValue: value || '',
		pageSource,
		onValueChange: (newValue) => emitChange(newValue),
	});
	const {
		suggestions,
		isLoading,
		isOpen,
		openSuggestions,
		closeSuggestions,
		selectSuggestion,
		error: fetchError,
	} = aiSuggestions;

	const errorMessage = error?.message?.toString() ?? '';
	const fieldId = useId(name);
	const labelId = `${fieldId}-label`;
	const errorId = `${fieldId}-error`;
	const helperTextId = error ? errorId : undefined;
	const suggestionsId = `${fieldId}-suggestions`;
	const describedBy = [helperTextId].filter(Boolean).join(' ') || undefined;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			<FormLabel id={labelId} htmlFor={fieldId} sx={{ color: palette.text.primary }}>
				{label}
			</FormLabel>
			<Autocomplete
				data-testid="ai-suggestions-autocomplete"
				filterOptions={(options) => options}
				freeSolo
				open={isOpen && suggestions.length > 0}
				onOpen={() => {}} // Manual open via AI button only
				onClose={closeSuggestions}
				options={suggestions}
				value={value || ''}
				onChange={(_event, newValue) => {
					if (typeof newValue === 'string') {
						selectSuggestion(newValue);
						emitChange(newValue, 'select');
					}
				}}
				disableClearable
				aria-labelledby={label ? labelId : undefined}
				renderOption={({ key, ...optionProps }, option) => (
					<OptionWithConditionalTooltip key={key} option={option} props={optionProps} />
				)}
				onInputChange={(_event, newInputValue, reason) => {
					if (reason === 'input') {
						emitChange(newInputValue, reason);
					}
				}}
				loading={isLoading}
				loadingText={
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 1.5,
							py: 2,
						}}
						role="status"
						aria-live="polite"
					>
						<CircularProgress size={24} aria-hidden="true" />
						<Typography variant="body2" color="text.secondary">
							{t('AI suggestions are being generated...')}
						</Typography>
					</Box>
				}
				noOptionsText={
					fetchError ? (
						<Typography variant="body2" color="error" role="alert">
							{t(fetchError)}
						</Typography>
					) : (
						<Typography variant="body2" color="text.secondary">
							{t('No suggestions available')}
						</Typography>
					)
				}
				ListboxProps={{
					id: suggestionsId,
					role: 'listbox',
					'aria-label': t('AI suggestions'),
				}}
				renderInput={(params) => (
					<TextField
						data-testid="ai-suggestions-field"
						{...params}
						id={fieldId}
						placeholder={placeholder}
						name={name}
						multiline={multiline}
						variant="outlined"
						inputRef={registration?.ref}
						helperText={t(errorMessage)}
						error={error ? true : false}
						minRows={multiline ? minRows : undefined}
						aria-labelledby={label ? labelId : undefined}
						aria-describedby={describedBy}
						aria-invalid={!!error || undefined}
						aria-autocomplete="list"
						aria-expanded={isOpen && suggestions.length > 0}
						aria-controls={suggestionsId}
						FormHelperTextProps={{
							id: helperTextId,
							role: error ? 'alert' : undefined,
						}}
						inputProps={{
							...params.inputProps,
							// The Autocomplete owns the input's blur; react-hook-form (mode: 'onTouched') needs it too.
							onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
								params.inputProps.onBlur?.(event);
								registration?.onBlur(event);
							},
							maxLength: maxLength,
							style: {
								minHeight: '24px',
								paddingTop: spaces.xs,
								paddingBottom: spaces.xs,
								paddingLeft: spaces.sm,
								paddingRight: spaces.sm,
							},
							'data-testid': dataTestId,
						}}
						sx={{
							'& .MuiOutlinedInput-root': {
								minHeight: '40px',
								padding: 0,
							},
							'& .MuiInputBase-input': {
								minHeight: '24px',
							},
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{params.InputProps.endAdornment}
									<Tooltip title={t('Get AI suggestions')} placement="top" arrow>
										<span>
											<IconButton
												data-testid="get-ai-suggestions-button"
												onClick={openSuggestions}
												disabled={isLoading}
												size="small"
												aria-label={t('Get AI suggestions')}
												aria-expanded={isOpen && suggestions.length > 0}
												aria-controls={suggestionsId}
												type="button"
												sx={{
													padding: spaces.xs,
													marginRight: spaces.xs,
													marginBottom: 'auto',
													borderRadius: borderRadius.xs,
													'&:hover': {
														backgroundColor: palette.action.hover,
													},
													'&:focus, &:focus-visible': {
														backgroundColor: palette.primary.focusVisible,
													},
												}}
											>
												{isLoading ? (
													<CircularProgress
														size="20px"
														aria-hidden="true"
														sx={{
															'& .MuiCircularProgress-svg': {
																height: '20px !important',
																width: '20px !important',
															},
														}}
													/>
												) : (
													<SvgIcon
														component={getMuiIcon('aiOutlined')}
														sx={{
															color: palette.primary.main,
															fontSize: '1.25rem',
														}}
														aria-hidden="true"
													/>
												)}
											</IconButton>
										</span>
									</Tooltip>
								</>
							),
							sx: {
								border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
								borderRadius: borderRadius.sm,
								minHeight: multiline && minRows > 1 ? 'auto' : '40px',
								padding: multiline && minRows > 1 ? `${spaces.xs} ${spaces.sm}` : 0,
							},
						}}
					/>
				)}
				PaperComponent={(paperProps) => (
					<Paper
						{...paperProps}
						sx={{
							marginTop: error ? '-16px' : '0px',
							borderRadius: borderRadius.xs,
							boxShadow: '0px 15px 20px -4px rgba(17,24,39,0.1), 0px 7px 8px 0px rgba(17,24,39,0.05)',
						}}
					/>
				)}
				PopperComponent={({ children, disablePortal, anchorEl, ...popperProps }) => (
					<Box
						{...popperProps}
						sx={{
							...popperProps.sx,
							zIndex: 1300,
						}}
					>
						{children as React.ReactNode}
					</Box>
				)}
			/>
		</Box>
	);
};

export default AISuggestionsField;
